import {
  MCP_TIME_SERIES_GRANULARITY,
  MCPTimeSeriesGranularity,
  Sentiment,
} from "@/constants";
import { DateFormatter } from "@/contexts/i18nContext";
import {
  MCPMonitorFilters,
  MCPMonitorSentimentDistribution,
} from "@/types/Search";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { formatDateForApi, maxKey } from "@/utils";
import {
  endOfDay,
  endOfHour,
  endOfMonth,
  endOfWeek,
  getWeek,
  parseISO,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

export function getMCPMonitorFiltersFromSearchFilterV4(
  filters: SearchFilterV4,
  diffInDays?: number
): MCPMonitorFilters {
  return {
    start_date: filters.start_date
      ? typeof diffInDays === "number"
        ? formatDateForApi(subDays(new Date(filters.start_date), diffInDays))
        : filters.start_date
      : undefined,
    end_date: filters.end_date
      ? typeof diffInDays === "number"
        ? formatDateForApi(subDays(new Date(filters.end_date), diffInDays))
        : filters.end_date
      : undefined,
    min_reach: filters.min_reach_threshold,
    channel_type: filters.channel_type,
    channel_name: filters.channel_name,
    sentiment: filters.sentiment,
    medium: filters.mention_kind
      ? Array.isArray(filters.mention_kind)
        ? filters.mention_kind[0]
        : filters.mention_kind
      : undefined,
    freetext: filters.freetext,
    language: filters.language,
  };
}

export function getMCPMonitorTimeSeriesGranularityFromDayDiff(
  diffInDays: number
): MCPTimeSeriesGranularity {
  if (diffInDays <= 1) {
    return MCP_TIME_SERIES_GRANULARITY.HOUR;
  }

  if (diffInDays <= 30) {
    return MCP_TIME_SERIES_GRANULARITY.DAY;
  }

  if (diffInDays <= 100) {
    return MCP_TIME_SERIES_GRANULARITY.WEEK;
  }

  return MCP_TIME_SERIES_GRANULARITY.MONTH;
}

export function formatDateFromMCPMonitorTimeSeriesGranularity(
  date: string,
  granularity: MCPTimeSeriesGranularity,
  format: DateFormatter
) {
  switch (granularity) {
    case MCP_TIME_SERIES_GRANULARITY.HOUR:
      return format(date, "HH:mm");
    case MCP_TIME_SERIES_GRANULARITY.DAY:
      return format(date, "d MMM");
    case MCP_TIME_SERIES_GRANULARITY.WEEK:
      return "v" + getWeek(date, { weekStartsOn: 1 }).toLocaleString();
    case MCP_TIME_SERIES_GRANULARITY.MONTH:
      return format(date, "MMMM yyyy");
  }
}

export function getPeriodFromStartDateAndGranularity(
  bucket: string,
  granularity: MCPTimeSeriesGranularity,
  timeZone: string
) {
  // Only hour buckets carry a time zone; the rest are bare dates already
  // expressed in the user's time zone.
  const zonedDate =
    granularity === MCP_TIME_SERIES_GRANULARITY.HOUR
      ? toZonedTime(parseISO(bucket), timeZone)
      : parseISO(bucket);

  const getStartDate = () => {
    switch (granularity) {
      case MCP_TIME_SERIES_GRANULARITY.HOUR:
        return startOfHour(zonedDate);
      case MCP_TIME_SERIES_GRANULARITY.DAY:
        return startOfDay(zonedDate);
      case MCP_TIME_SERIES_GRANULARITY.WEEK:
        return startOfWeek(zonedDate, { weekStartsOn: 1 });
      case MCP_TIME_SERIES_GRANULARITY.MONTH:
        return startOfMonth(zonedDate);
    }
  };

  const getEndDate = () => {
    switch (granularity) {
      case MCP_TIME_SERIES_GRANULARITY.HOUR:
        return endOfHour(zonedDate);
      case MCP_TIME_SERIES_GRANULARITY.DAY:
        return endOfDay(zonedDate);
      case MCP_TIME_SERIES_GRANULARITY.WEEK:
        return endOfWeek(zonedDate, { weekStartsOn: 1 });
      case MCP_TIME_SERIES_GRANULARITY.MONTH:
        return endOfMonth(zonedDate);
    }
  };

  return {
    startDate: fromZonedTime(getStartDate(), timeZone),
    endDate: fromZonedTime(getEndDate(), timeZone),
  };
}

export const getOverallSentiment = (
  shares: MCPMonitorSentimentDistribution["shares"]
): Sentiment | undefined => {
  const { unknown: _unknown, ...sentimentShares } = shares;
  const max = maxKey(sentimentShares);

  if (Array.isArray(max)) return undefined;
  return max;
};
